import {
  compose,
  flattenProp,
  withStateHandlers,
  withHandlers,
} from 'recompose';
import { connect } from 'react-redux';
import { addProps, addIntlProps } from 'utils/recompose';
import { injectIntl } from 'react-intl';
import {
  template,
  functionalGroupTemplates,
  designPackageForTemplate,
} from 'reducers/templates/selectors';
import { inReviewTemplateIds } from 'reducers/models/selectors';
import { theme } from 'reducers/dashboard/selectors';
import combineSelectors from 'utils/combineSelectors';
import { assign, values, mapValues, without, every, pickBy } from 'lodash';
import formatCurrency from 'utils/formatCurrency';
import messages from './messages';
import { setSwappedTemplateId } from 'reducers/templates/actions';
import { includes } from 'lodash';

const mapStateToProps = combineSelectors({
  template,
  functionalGroupTemplates,
  designPackageForTemplate,
  inReviewTemplateIds,
  theme,
});

const setSelectedTemplate = () => selectedTemplateId => ({
  selectedTemplateId,
});

const similarTemplates = ({
  template: { designPackage },
  functionalGroupTemplates,
  inReviewTemplateIds,
  designPackageForTemplate,
  selectedDesignOption,
}) => {
  let { optionKeys } = designPackageForTemplate;
  let matchingKeys = without(optionKeys, selectedDesignOption);

  let similarTemplates = pickBy(functionalGroupTemplates, fgt => {
    return (
      every(matchingKeys, key => {
        return designPackage[key].id === fgt.designPackage[key].id;
      }) && !includes(inReviewTemplateIds, fgt.id)
    );
  });
  return similarTemplates;
};

export const price = ({ selectedDesignOption, selectedTemplate, template }) => {
  let st = selectedTemplate || template;
  return st.designPackage[selectedDesignOption].upcharge || 0;
};

export const alternativeDesigns = ({
  similarTemplates,
  selectedDesignOption,
}) => {
  let designs = values(
    mapValues(similarTemplates, st => {
      return assign(st[selectedDesignOption], { templateId: st.id });
    }),
  );
  return designs;
};

export const description = ({
  selectedTemplate,
  selectedDesignOption,
  template,
}) => {
  let st = selectedTemplate || template;
  return st[selectedDesignOption].description;
};

const designPriceFormatted = formatCurrency({
  priceProp: 'price',
  currencyProp: 'currency',
});

const prefix = ({
  selectedDesignOption,
  wheelName,
  trimName,
  exteriorName,
  interiorName,
}) => {
  let mapping = {
    paintOption: exteriorName,
    trimOption: trimName,
    interiorOption: interiorName,
    wheelOption: wheelName,
  };
  return mapping[selectedDesignOption];
};

const selectTemplateId = ({
  setSwappedTemplateId,
  setSelectedTemplate,
}) => templateId => {
  setSwappedTemplateId(templateId);
  setSelectedTemplate(templateId);
};

const selectedTemplate = ({
  similarTemplates,
  selectedTemplateId,
  template,
}) => {
  let id = selectedTemplateId || template.id;
  return similarTemplates[id];
};

const priceDifference = ({
  selectedTemplateId,
  similarTemplates,
  template,
}) => {
  if (!selectedTemplateId || template.id === selectedTemplateId) {
    return 0;
  } else {
    let oldPrice = template.designPrice;
    let newPrice = similarTemplates[selectedTemplateId].designPrice;
    return newPrice - oldPrice;
  }
};

const priceMessage = ({ priceDifference, increasePrice, decreasePrice }) => {
  if (priceDifference === 0) {
    return '';
  } else if (priceDifference > 0) {
    return increasePrice;
  } else {
    return decreasePrice;
  }
};

const priceDifferenceAbs = ({ priceDifference }) => Math.abs(priceDifference);
const formattedPriceDifference = formatCurrency({
  priceProp: 'priceDifferenceAbs',
  currencyProp: 'currency',
});

export const textStyle = ({ theme }) => ({ color: theme.dropDownText });

export default compose(
  injectIntl,
  addIntlProps({
    messages,
    propNames: [
      'currency',
      'wheelName',
      'trimName',
      'exteriorName',
      'interiorName',
      'increasePrice',
      'decreasePrice',
      'changePriceBand',
    ],
  }),
  connect(
    mapStateToProps,
    { setSwappedTemplateId },
  ),
  flattenProp('template'),
  withStateHandlers({ selectedTemplateId: null }, { setSelectedTemplate }),
  withHandlers({
    selectTemplateId,
  }),
  addProps({ similarTemplates }),
  addProps({ selectedTemplate }),
  addProps({ price }),
  addProps({
    alternativeDesigns,
    description,
    designPriceFormatted,
    prefix,
    priceDifference,
  }),
  addProps({ priceDifferenceAbs }),
  addProps({
    formattedPriceDifference,
    priceMessage,
    textStyle,
  }),
);
